import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { ALERT_CATEGORIES, SEVERITY_STYLES, severityFor } from "@/components/vendor/alerts";
import { toLocalIso as toIso, fromLocalIso as fromIso, formatLocalDay, localToday } from "@/lib/localDate";

const formatDay = (iso) => formatLocalDay(iso, { fallback: "Pick a date" });

// Form section rendered inside CarForm. Lets vendors set/clear a date and an optional note
// per category. Date input doubles as a "current status" indicator showing the severity pill.
export default function CarAlertsEditor({ value = {}, onChange }) {
  const update = (field, v) => onChange({ ...(value || {}), [field]: v });
  const today = localToday();

  return (
    <div className="rounded-2xl border border-border p-4 bg-white space-y-4" data-testid="alerts-editor">
      <div>
        <Label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Compliance alerts</Label>
        <p className="text-xs text-neutral-500 mt-1">
          Track expiry dates per category. The dashboard pulses a colored pill when a date is within 30 days, or red once it's overdue.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {ALERT_CATEGORIES.map(({ key, dateField, noteField, label, icon: Icon }) => {
          const dateVal = value[dateField] || "";
          const noteVal = value[noteField] || "";
          const { severity } = severityFor(dateVal);
          const s = SEVERITY_STYLES[severity];
          return (
            <div key={key} className="space-y-2" data-testid={`alert-row-${key}`}>
              <div className="flex items-center gap-2">
                <Icon className="h-3.5 w-3.5 text-neutral-500" />
                <span className="text-sm font-medium">{label}</span>
                <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full border ${s.bg} ${s.text} ${s.border}`}>
                  {s.label}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center justify-between gap-2 rounded-lg border border-input bg-white px-2.5 py-1.5 text-xs hover:border-neutral-400 transition w-full"
                      data-testid={`alert-date-${key}`}
                    >
                      <span className={dateVal ? "text-neutral-900" : "text-neutral-400"}>{formatDay(dateVal)}</span>
                      <CalendarIcon className="h-3.5 w-3.5 text-neutral-500" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={fromIso(dateVal)}
                      onSelect={(d) => update(dateField, toIso(d))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {dateVal && (
                  <button
                    type="button"
                    onClick={() => update(dateField, "")}
                    className="text-neutral-400 hover:text-rose-600 transition shrink-0"
                    aria-label="Clear date"
                    data-testid={`alert-clear-${key}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <Input
                value={noteVal}
                onChange={(e) => update(noteField, e.target.value)}
                placeholder="Note (optional) — insurer name, policy #, garage…"
                className="rounded-lg text-xs h-8"
                data-testid={`alert-note-${key}`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
